import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Eye,
  EyeOff,
  ArrowLeft,
  Shield,
  Heart,
  Lock,
  Mail,
  CheckCircle2,
  KeyRound,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { user, login, forgotPassword, resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Forgot Password Modal State
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1 = enter email, 2 = enter otp & new pass
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  // If already logged in, redirect to the appropriate portal
  useEffect(() => {
    if (user) {
      if (user.role === 'admin') {
        navigate('/admin', { replace: true });
      } else {
        navigate('/portal', { replace: true });
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const loggedUser = await login(email.trim(), password);
      
      if (loggedUser.role === 'admin') {
        toast.success('Welcome to Charitage Admin Control Panel!');
        navigate('/admin');
      } else {
        toast.success(`Welcome back, ${loggedUser.name || 'Donor'}!`);
        navigate('/portal');
      }
    } catch (error) {
      console.error('Login failed:', error);
      const msg = error.response?.data?.detail || error.response?.data?.error || error.message || 'Invalid credentials. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForgot = () => {
    setResetEmail(email.trim());
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setForgotStep(1);
    setForgotOpen(true);
  };

  const handleSendResetCode = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setForgotLoading(true);
    try {
      await forgotPassword(resetEmail.trim());
      toast.success(`Verification code sent to ${resetEmail.trim()}! Please check your inbox.`);
      setForgotStep(2);
    } catch (error) {
      console.error('Forgot password error:', error);
      const msg = error.response?.data?.detail || 'Could not send reset code. Please try again.';
      toast.error(msg);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!resetCode.trim() || resetCode.trim().length !== 6) {
      toast.error('Please enter the 6-digit verification code sent to your email.');
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      toast.error('New password must be at least 6 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match. Please verify.');
      return;
    }

    setForgotLoading(true);
    try {
      await resetPassword(resetEmail.trim(), resetCode.trim(), newPassword);
      toast.success('Password updated successfully! Signing you in...');

      // Prepopulate login credentials
      setEmail(resetEmail.trim());
      setPassword(newPassword);
      setForgotOpen(false);

      // Attempt immediate auto-login
      try {
        const loggedUser = await login(resetEmail.trim(), newPassword);
        if (loggedUser.role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/portal');
        }
      } catch {
        toast.info('Please click Sign In to continue.');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      const msg = error.response?.data?.detail || 'Invalid or expired code. Please try again.';
      toast.error(msg);
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-primary to-slate-950 flex flex-col justify-between p-4 sm:p-6 lg:p-8 font-body relative overflow-hidden">
      {/* Background Decorative Accents */}
      <div className="absolute top-0 right-0 -mr-24 -mt-24 w-96 h-96 rounded-full bg-secondary/15 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -ml-24 -mb-24 w-96 h-96 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />

      {/* Top Header Bar */}
      <header className="w-full max-w-6xl mx-auto flex items-center justify-between z-10 py-2">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-semibold transition-colors bg-white/10 hover:bg-white/15 px-4 py-2 rounded-full backdrop-blur-sm border border-white/10"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Main Website</span>
        </Link>

        <div className="hidden sm:flex items-center gap-2 text-xs text-white/70 bg-white/5 px-3 py-1.5 rounded-full border border-white/10">
          <Shield className="w-3.5 h-3.5 text-secondary" />
          <span>256-Bit SSL Secure Portal</span>
        </div>
      </header>

      {/* Main Login Card */}
      <main className="w-full max-w-md mx-auto my-auto z-10 py-6">
        <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 p-8 sm:p-10 relative">
          {/* Logo & Heading */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-4">
              <img
                src="/logo-landscape.png"
                alt="Charitage Foundation"
                className="h-12 mx-auto object-contain"
              />
            </Link>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold text-slate-900 tracking-tight">
              Portal Sign In
            </h1>
            <p className="text-slate-500 text-sm mt-2">
              Sign in to manage your donations, tax receipts & activities
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="login-email" className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-2">
                Email Address
              </Label>
              <div className="relative">
                <Input
                  id="login-email"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  className="pl-11 h-12 rounded-xl border-slate-200 focus:border-primary focus:ring-primary/20 text-slate-900 text-sm"
                />
                <Mail className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="login-password" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Password
                </Label>
                <button
                  type="button"
                  onClick={handleOpenForgot}
                  className="text-xs font-semibold text-secondary hover:text-secondary/80 hover:underline transition-colors"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-11 pr-11 h-12 rounded-xl border-slate-200 focus:border-primary focus:ring-primary/20 text-slate-900 text-sm"
                />
                <Lock className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="p-1.5 text-slate-400 hover:text-slate-700 absolute right-3 top-1/2 -translate-y-1/2 rounded-lg"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary hover:bg-primary/95 text-white font-bold text-base rounded-xl shadow-lg shadow-primary/20 transition-all transform hover:scale-[1.01] active:scale-[0.99] mt-2"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Signing In...</span>
                </span>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Divider & Register Link */}
          <div className="mt-8 pt-6 border-t border-slate-100 text-center">
            <p className="text-sm text-slate-600">
              Don't have a donor account yet?{' '}
              <Link
                to="/register"
                className="font-bold text-secondary hover:text-secondary/80 transition-colors"
              >
                Join & Register
              </Link>
            </p>
          </div>

          {/* Role Access Indicator */}
          <div className="mt-6 bg-slate-50 rounded-xl p-3 border border-slate-100 flex items-center gap-3 text-left">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <p className="text-xs text-slate-600">
              Universal Login: Both <span className="font-semibold text-slate-900">Donors</span> and <span className="font-semibold text-slate-900">System Admins</span> are automatically directed to their respective dashboards.
            </p>
          </div>
        </div>
      </main>

      {/* Forgot Password Modal Dialog */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="sm:max-w-md bg-white rounded-3xl p-6 sm:p-8 shadow-2xl border border-slate-100">
          <DialogHeader className="text-center sm:text-center pb-2">
            <div className="mx-auto w-12 h-12 rounded-2xl bg-amber-50 border border-amber-200/60 flex items-center justify-center text-amber-600 mb-3 shadow-inner">
              <KeyRound className="w-6 h-6" />
            </div>
            <DialogTitle className="text-xl sm:text-2xl font-heading font-extrabold text-slate-900">
              {forgotStep === 1 ? 'Reset Your Password' : 'Verify & Set New Password'}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500 mt-1.5">
              {forgotStep === 1
                ? 'Enter your registered email address and we will send you a 6-digit verification code.'
                : `We sent a 6-digit verification code to ${resetEmail}. Enter it below along with your new password.`}
            </DialogDescription>
          </DialogHeader>

          {forgotStep === 1 ? (
            <form onSubmit={handleSendResetCode} className="space-y-4 mt-2">
              <div>
                <Label htmlFor="reset-email" className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-2">
                  Registered Email
                </Label>
                <div className="relative">
                  <Input
                    id="reset-email"
                    type="email"
                    required
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="name@example.com"
                    className="pl-11 h-12 rounded-xl border-slate-200 focus:border-primary focus:ring-primary/20 text-slate-900 text-sm"
                  />
                  <Mail className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>

              <Button
                type="submit"
                disabled={forgotLoading || !resetEmail.trim()}
                className="w-full h-12 bg-primary hover:bg-primary/95 text-white font-bold text-sm rounded-xl shadow-md transition-all mt-4"
              >
                {forgotLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Sending Reset Code...</span>
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <span>Send Reset Code</span>
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4 mt-2">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="reset-code" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    6-Digit Verification Code
                  </Label>
                  <button
                    type="button"
                    onClick={handleSendResetCode}
                    disabled={forgotLoading}
                    className="text-xs text-secondary hover:underline font-semibold inline-flex items-center gap-1"
                  >
                    <RefreshCw className={`w-3 h-3 ${forgotLoading ? 'animate-spin' : ''}`} />
                    <span>Resend Code</span>
                  </button>
                </div>
                <Input
                  id="reset-code"
                  type="text"
                  required
                  maxLength={6}
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="• • • • • •"
                  className="h-12 text-center text-2xl font-mono font-black tracking-[0.4em] rounded-xl border-slate-200 focus:border-primary text-slate-900"
                />
              </div>

              <div>
                <Label htmlFor="reset-new-password" className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-2">
                  New Password
                </Label>
                <div className="relative">
                  <Input
                    id="reset-new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimum 6 characters"
                    className="pl-11 pr-11 h-12 rounded-xl border-slate-200 focus:border-primary text-slate-900 text-sm"
                  />
                  <Lock className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="p-1.5 text-slate-400 hover:text-slate-700 absolute right-3 top-1/2 -translate-y-1/2 rounded-lg"
                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="reset-confirm-password" className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-2">
                  Confirm New Password
                </Label>
                <div className="relative">
                  <Input
                    id="reset-confirm-password"
                    type={showNewPassword ? 'text' : 'password'}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                    className="pl-11 pr-11 h-12 rounded-xl border-slate-200 focus:border-primary text-slate-900 text-sm"
                  />
                  <Lock className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setForgotStep(1)}
                  className="w-1/3 h-12 rounded-xl border-slate-200 text-slate-700 text-xs font-semibold"
                >
                  Change Email
                </Button>
                <Button
                  type="submit"
                  disabled={forgotLoading || !resetCode.trim() || !newPassword || !confirmPassword}
                  className="w-2/3 h-12 bg-primary hover:bg-primary/95 text-white font-bold text-sm rounded-xl shadow-md transition-all"
                >
                  {forgotLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Saving...</span>
                    </span>
                  ) : (
                    'Reset & Sign In'
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer Note */}
      <footer className="w-full max-w-6xl mx-auto text-center z-10 py-2">
        <p className="text-xs text-white/50">
          © {new Date().getFullYear()} Charitage Foundation. All rights reserved. Leading with Kindness.
        </p>
      </footer>
    </div>
  );
};

export default LoginPage;
